import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../../services/api';
import deliveryService, {
  DeliveryAddress,
  formatDeliveryAddressLine,
} from '../../services/deliveryService';
import PinDropMap from './PinDropMap';
import {
  buildingDetailsOf,
  placeDetails,
  reverseGeocode,
  searchPlaces,
  streetLocalityOf,
  type GeocodedPlace,
  type PlaceSuggestion,
} from './geocode';
import { isGoogleMapsBlocked, loadGoogleMaps } from './googleMaps';
import './DeliveryAddressBook.css';

const ADDRESS_TYPES = ['Home', 'Work', 'Other'] as const;
type FlowStep = 'list' | 'locate' | 'details';

const PHONE_COUNTRY_CODES = [
  { code: '+91', label: 'IN +91' },
  { code: '+1', label: 'US/CA +1' },
  { code: '+44', label: 'UK +44' },
  { code: '+971', label: 'AE +971' },
  { code: '+65', label: 'SG +65' },
  { code: '+61', label: 'AU +61' },
  { code: '+94', label: 'LK +94' },
  { code: '+977', label: 'NP +977' },
  { code: '+880', label: 'BD +880' },
  { code: '+92', label: 'PK +92' },
];

const SORTED_PHONE_CODES = PHONE_COUNTRY_CODES.map((row) => row.code).sort((a, b) => b.length - a.length);

function nationalPhone(phone?: string, code?: string): string {
  let value = (phone || '').trim();
  if (code && value.indexOf(code) === 0) {
    return value.slice(code.length).replace(/^\s+/, '');
  }
  for (let i = 0; i < SORTED_PHONE_CODES.length; i += 1) {
    const prefix = SORTED_PHONE_CODES[i];
    if (value.indexOf(prefix) === 0) {
      return value.slice(prefix.length).replace(/^\s+/, '');
    }
  }
  return value.replace(/^\+/, '');
}

function displayPhone(address: DeliveryAddress): string {
  const code = address.phoneCountryCode || '+91';
  const national = nationalPhone(address.phone, code);
  return national ? `${code} ${national}` : code;
}

const emptyForm = (defaults?: { fullName?: string; phone?: string; isDefault?: boolean }): DeliveryAddress => ({
  addressType: 'Home',
  fullName: defaults?.fullName || '',
  phone: nationalPhone(defaults?.phone, '+91'),
  phoneCountryCode: '+91',
  area: '',
  locality: '',
  landmark: '',
  pincode: '',
  city: '',
  state: '',
  country: '',
  floor: '',
  tower: '',
  building: '',
  instructions: '',
  lat: '',
  lon: '',
  geocodeStatus: '',
  isDefault: Boolean(defaults?.isDefault),
});

function applyPlace(prev: DeliveryAddress, place: GeocodedPlace, overwriteStreet: boolean): DeliveryAddress {
  const street = [place.area, place.locality].filter(Boolean).join(', ');
  return {
    ...prev,
    lat: String(place.lat),
    lon: String(place.lon),
    geocodeStatus: place.geocodeStatus,
    placeId: place.placeId || prev.placeId,
    city: place.city || '',
    state: place.state || '',
    pincode: place.pincode || '',
    country: place.country || '',
    area: overwriteStreet || !prev.area ? street || prev.area : prev.area,
    locality: overwriteStreet ? '' : prev.locality,
  };
}

type DeliveryAddressBookProps = {
  selectable?: boolean;
  selectedId?: number | null;
  onSelect?: (id: number | null) => void;
  onPick?: (id: number) => void;
  onListChange?: (addresses: DeliveryAddress[]) => void;
  defaultName?: string;
  defaultPhone?: string;
  defaultCenter?: { lat: number; lon: number };
};

const DeliveryAddressBook: React.FC<DeliveryAddressBookProps> = ({
  selectable = false,
  selectedId = null,
  onSelect,
  onPick,
  onListChange,
  defaultName,
  defaultPhone,
  defaultCenter,
}) => {
  const [addresses, setAddresses] = useState<DeliveryAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<FlowStep>('list');
  const [form, setForm] = useState<DeliveryAddress>(() => emptyForm({ fullName: defaultName, phone: defaultPhone }));
  const [locationLabel, setLocationLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [locating, setLocating] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [placeSuggestions, setPlaceSuggestions] = useState<PlaceSuggestion[]>([]);
  const [searchingPlaces, setSearchingPlaces] = useState(false);
  const [mapsKey, setMapsKey] = useState('');
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsHint, setMapsHint] = useState<string | null>(null);
  const [mapInteractive, setMapInteractive] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const suppressPlaceSearchRef = useRef(false);

  const placeBias = useMemo(() => {
    const pinLat = form.lat ? Number(form.lat) : NaN;
    const pinLon = form.lon ? Number(form.lon) : NaN;
    if (Number.isFinite(pinLat) && Number.isFinite(pinLon)) {
      return { lat: pinLat, lng: pinLon };
    }
    if (defaultCenter) {
      return { lat: defaultCenter.lat, lng: defaultCenter.lon };
    }
    return { lat: 12.9716, lng: 77.5946 };
  }, [form.lat, form.lon, defaultCenter]);
  const placeBiasRef = useRef(placeBias);
  placeBiasRef.current = placeBias;

  const defaults = useMemo(
    () => ({ fullName: defaultName, phone: defaultPhone }),
    [defaultName, defaultPhone]
  );

  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/api/media/config')
      .then((res) => {
        const key = String(res?.data?.googleMapsApiKey || '').trim();
        if (cancelled) {
          return;
        }
        setMapsKey(key);
        if (!key) {
          setMapsHint('Address search is not configured. Drop a pin on the map instead.');
          return;
        }
        return loadGoogleMaps(key).then(() => {
          if (!cancelled && !isGoogleMapsBlocked()) {
            setMapsReady(true);
            setMapsHint(null);
          }
        });
      })
      .catch(() => {
        if (!cancelled) {
          setMapsHint('Could not load Google Maps. Drop a pin on the map instead.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await deliveryService.listUserAddresses();
      const list = res.addresses || [];
      setAddresses(list);
      onListChange?.(list);
      const preferred = list.find((row) => row.isDefault) || list[0];
      if (selectable && preferred?.id && selectedIdRef.current == null) {
        onSelect?.(preferred.id);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Could not load addresses');
    } finally {
      setLoading(false);
    }
  }, [onListChange, onSelect, selectable]);

  useEffect(() => {
    loadAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = step === 'list' ? '' : 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [step]);

  const applyPlaceToForm = async (place: GeocodedPlace, overwriteStreet: boolean) => {
    setForm((prev) => applyPlace(prev, place, overwriteStreet));
    setLocationLabel(place.label);
    setError(null);
  };

  const applyPlaceToFormRef = useRef(applyPlaceToForm);
  applyPlaceToFormRef.current = applyPlaceToForm;

  useEffect(() => {
    if (step !== 'locate' || !mapsReady || !mapsKey || isGoogleMapsBlocked()) {
      setPlaceSuggestions([]);
      setSearchingPlaces(false);
      return;
    }
    const q = searchQuery.trim();
    if (suppressPlaceSearchRef.current) {
      suppressPlaceSearchRef.current = false;
      setPlaceSuggestions([]);
      setSearchingPlaces(false);
      return;
    }
    if (q.length < 3) {
      setPlaceSuggestions([]);
      setSearchingPlaces(false);
      return;
    }
    let cancelled = false;
    setSearchingPlaces(true);
    const timer = window.setTimeout(() => {
      searchPlaces(mapsKey, q, placeBiasRef.current)
        .then((rows) => {
          if (!cancelled) {
            setPlaceSuggestions(rows);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setPlaceSuggestions([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearchingPlaces(false);
          }
        });
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [step, mapsReady, mapsKey, searchQuery]);

  const selectSuggestion = async (suggestion: PlaceSuggestion) => {
    if (!mapsKey) {
      return;
    }
    setPlaceSuggestions([]);
    suppressPlaceSearchRef.current = true;
    setSearchQuery(suggestion.label);
    const place = await placeDetails(mapsKey, suggestion.placeId);
    if (!place) {
      setError('Could not load that place. Try another result or drop a pin.');
      return;
    }
    await applyPlaceToFormRef.current(place, true);
  };

  const closeFlow = () => {
    setStep('list');
    setForm(emptyForm({ ...defaults, isDefault: addresses.length === 0 }));
    setLocationLabel('');
    setSearchQuery('');
    setPlaceSuggestions([]);
    setError(null);
  };

  const openNew = () => {
    setForm(emptyForm({ ...defaults, isDefault: addresses.length === 0 }));
    setLocationLabel('');
    setSearchQuery('');
    setPlaceSuggestions([]);
    setError(null);
    setStep('locate');
  };

  const openEdit = (address: DeliveryAddress) => {
    const code = address.phoneCountryCode || '+91';
    setForm({
      ...emptyForm(defaults),
      ...address,
      phoneCountryCode: code,
      phone: nationalPhone(address.phone, code),
    });
    setLocationLabel(formatDeliveryAddressLine(address));
    setSearchQuery('');
    setError(null);
    setStep(address.lat && address.lon ? 'details' : 'locate');
  };

  const applyPin = async (lat: number, lon: number) => {
    let key = mapsKey;
    if (!key) {
      try {
        const res = await apiClient.get('/api/media/config');
        key = String(res?.data?.googleMapsApiKey || '').trim();
        if (key) {
          setMapsKey(key);
        }
      } catch {
        key = '';
      }
    }
    if (!key) {
      setError('Google Maps is not ready. Try again in a moment.');
      await applyPlaceToForm(
        { lat, lon, label: `${lat.toFixed(5)}, ${lon.toFixed(5)}`, geocodeStatus: 'PIN_ONLY' },
        true
      );
      return;
    }
    const place = await reverseGeocode(key, lat, lon);
    await applyPlaceToForm(place, true);
    suppressPlaceSearchRef.current = true;
    setSearchQuery(place.label);
    setPlaceSuggestions([]);
    if (!place.city?.trim() || !place.state?.trim() || !place.pincode?.trim()) {
      setError('Could not read city, state and pincode for this location. Move the pin slightly and try again.');
    }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setError('Location is not available on this device');
      return;
    }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await applyPin(pos.coords.latitude, pos.coords.longitude);
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        setError('Could not get your location. Search or drop a pin instead.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSave = async () => {
    if (!form.lat || !form.lon) {
      setError('Search or drop a pin on the map first');
      setStep('locate');
      return;
    }
    if (!buildingDetailsOf(form).trim()) {
      setError('Enter flat / floor / tower / building');
      return;
    }
    if (!streetLocalityOf(form).trim()) {
      setError('Street / locality is required');
      return;
    }
    if (!form.fullName?.trim() || !form.phone?.trim()) {
      setError('Name and phone are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const lat = Number(form.lat);
      const lon = Number(form.lon);
      const geo = mapsKey && Number.isFinite(lat) && Number.isFinite(lon)
        ? await reverseGeocode(mapsKey, lat, lon)
        : null;
      const resolved = geo ? applyPlace(form, geo, false) : form;
      if (!resolved.pincode?.trim() || !resolved.city?.trim() || !resolved.state?.trim()) {
        setError('Could not read city, state and pincode for this pin. Move the pin and try again.');
        setSaving(false);
        setStep('locate');
        return;
      }
      const payload: DeliveryAddress = {
        ...resolved,
        floor: buildingDetailsOf(form),
        tower: '',
        building: '',
        area: streetLocalityOf(form),
        locality: '',
        city: geo?.city || resolved.city,
        state: geo?.state || resolved.state,
        pincode: geo?.pincode || resolved.pincode,
        country: geo?.country || resolved.country || '',
        geocodeStatus: geo?.geocodeStatus || resolved.geocodeStatus,
        placeId: geo?.placeId || resolved.placeId,
        phoneCountryCode: form.phoneCountryCode || '+91',
        phone: nationalPhone(form.phone, form.phoneCountryCode || '+91'),
        isDefault: Boolean(form.isDefault) || addresses.length === 0,
      };
      const saved = await deliveryService.saveUserAddress(payload);
      if (saved.success && saved.address) {
        const list = await deliveryService.listUserAddresses();
        setAddresses(list.addresses || []);
        onListChange?.(list.addresses || []);
        if (saved.address.id) {
          onSelect?.(saved.address.id);
          onPick?.(saved.address.id);
        }
        closeFlow();
      } else {
        setError(saved.message || 'Could not save address');
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Could not save address');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setError(null);
    try {
      await deliveryService.deleteUserAddress(id);
      const list = (await deliveryService.listUserAddresses()).addresses || [];
      setAddresses(list);
      onListChange?.(list);
      if (selectedId === id) {
        const preferred = list.find((row) => row.isDefault) || list[0];
        onSelect?.(preferred?.id || null);
      }
      setConfirmDeleteId(null);
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Could not delete address');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetDefault = async (id: number) => {
    setSettingDefaultId(id);
    setError(null);
    try {
      await deliveryService.setDefaultUserAddress(id);
      const list = (await deliveryService.listUserAddresses()).addresses || [];
      setAddresses(list);
      onListChange?.(list);
      if (selectable) {
        onSelect?.(id);
      }
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Could not set default address');
    } finally {
      setSettingDefaultId(null);
    }
  };

  const updateField = (field: keyof DeliveryAddress, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const hasPin = Boolean(form.lat && form.lon);
  const canSave =
    hasPin &&
    Boolean(buildingDetailsOf(form).trim()) &&
    Boolean(streetLocalityOf(form).trim()) &&
    Boolean(form.fullName?.trim()) &&
    Boolean(form.phone?.trim());

  const renderAddressBody = (address: DeliveryAddress) => (
    <>
      <span className="address-book__type-row">
        <span className="address-book__type">{address.addressType || 'Home'}</span>
        {address.isDefault && <span className="address-book__default-badge">Default</span>}
      </span>
      <strong>{address.fullName || address.addressType || 'Saved address'}</strong>
      <span className="address-book__line">{formatDeliveryAddressLine(address)}</span>
      {address.phone && <span className="address-book__phone">Phone: {displayPhone(address)}</span>}
    </>
  );

  return (
    <div className="address-book">
      <button type="button" className="address-book__add-card" onClick={openNew}>
        <span className="address-book__add-plus" aria-hidden>
          +
        </span>
        <span>Add address</span>
        <i className="fa fa-chevron-right" aria-hidden />
      </button>

      {loading ? (
        <p className="address-book__status">Loading addresses…</p>
      ) : addresses.length === 0 ? (
        <p className="address-book__empty">No saved addresses yet.</p>
      ) : (
        <>
          <p className="address-book__section-label">Saved addresses</p>
          <ul className="address-book__list">
            {addresses.map((address) => {
              const selected = selectable && selectedId === address.id;
              return (
                <li key={address.id} className={`address-book__item${selected ? ' is-selected' : ''}`}>
                  {selectable ? (
                    <button
                      type="button"
                      className="address-book__item-main"
                      onClick={() => {
                        if (!address.id) {
                          return;
                        }
                        onSelect?.(address.id);
                        onPick?.(address.id);
                      }}
                    >
                      {renderAddressBody(address)}
                    </button>
                  ) : (
                    <div className="address-book__item-main">{renderAddressBody(address)}</div>
                  )}
                  <div className="address-book__item-actions">
                    {!address.isDefault && address.id && (
                      <button
                        type="button"
                        disabled={settingDefaultId === address.id}
                        onClick={() => handleSetDefault(address.id as number)}
                      >
                        {settingDefaultId === address.id ? 'Setting…' : 'Set as default'}
                      </button>
                    )}
                    <button type="button" onClick={() => openEdit(address)}>
                      Edit
                    </button>
                    {confirmDeleteId === address.id ? (
                      <>
                        <button
                          type="button"
                          className="is-danger"
                          disabled={deletingId === address.id}
                          onClick={() => address.id && handleDelete(address.id)}
                        >
                          {deletingId === address.id ? 'Deleting…' : 'Confirm'}
                        </button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)}>
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button type="button" onClick={() => address.id && setConfirmDeleteId(address.id)}>
                        Delete
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {step !== 'list' && (
        <div className="address-flow-overlay">
          <div className="address-flow" role="dialog" aria-modal="true" aria-labelledby="address-flow-title">
          <div className="address-flow__bar">
            <button type="button" className="address-flow__back" onClick={step === 'details' ? () => setStep('locate') : closeFlow}>
              <i className="fa fa-arrow-left" aria-hidden />
            </button>
            <h2 id="address-flow-title">{step === 'locate' ? 'Select delivery location' : 'Add address details'}</h2>
          </div>

          {step === 'locate' && (
            <div className="address-flow__locate">
              <div className="address-book__search-wrap">
                <div className="address-book__search">
                  <i className="fa fa-search" aria-hidden />
                  <input
                    ref={searchInputRef}
                    className="address-book__search-input"
                    placeholder="Search for area, street or landmark"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoComplete="off"
                  />
                </div>
                {placeSuggestions.length > 0 && (
                  <ul className="address-book__search-results" role="listbox">
                    {placeSuggestions.map((row) => (
                      <li key={row.placeId}>
                        <button type="button" onClick={() => void selectSuggestion(row)}>
                          <strong>{row.label}</strong>
                          {row.secondary && row.secondary !== row.label ? <em>{row.secondary}</em> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {searchingPlaces && searchQuery.trim().length >= 3 && placeSuggestions.length === 0 && (
                  <p className="address-book__search-status">Searching places…</p>
                )}
              </div>

              <PinDropMap
                tall
                apiKey={mapsKey}
                lat={form.lat}
                lon={form.lon}
                defaultCenter={defaultCenter}
                onReady={() => setMapInteractive(true)}
                onBlocked={() => {
                  setMapInteractive(false);
                  setMapsHint('Map is not available on this site yet. Use current location to continue.');
                }}
                onPin={(lat, lon) => {
                  applyPin(lat, lon);
                }}
              />

              <button type="button" className="address-book__locate" onClick={handleUseMyLocation} disabled={locating}>
                {locating ? 'Fetching address…' : 'Use current location'}
              </button>

              {mapsHint && <p className="address-book__error address-book__error--flow">{mapsHint}</p>}
              {locationLabel && (
                <p className="address-flow__picked">
                  {locationLabel}
                  {(form.city || form.state || form.pincode || form.country) && (
                    <span>
                      {[form.city, form.state, form.pincode, form.country].filter(Boolean).join(', ')}
                    </span>
                  )}
                </p>
              )}

              <button
                type="button"
                className="address-book__save"
                disabled={!hasPin}
                onClick={() => {
                  if (hasPin) {
                    setStep('details');
                  }
                }}
              >
                Confirm location
              </button>
            </div>
          )}

          {step === 'details' && (
            <div className="address-flow__details">
              <div className="address-flow__picked-card">
                <i className="fa fa-map-marker" aria-hidden />
                <div>
                  <strong>{locationLabel || 'Pinned location'}</strong>
                  <p>{streetLocalityOf(form) || locationLabel}</p>
                  {(form.city || form.state || form.pincode || form.country) && (
                    <p>{[form.city, form.state, form.pincode, form.country].filter(Boolean).join(', ')}</p>
                  )}
                </div>
                <button type="button" className="address-flow__change" onClick={() => setStep('locate')}>
                  Change
                </button>
              </div>

              <label className="address-book__field">
                <span>Flat no. / Floor / Tower / Building</span>
                <input
                  className="address-book__input"
                  placeholder="E.g. 402, 4th floor, Tower B, DSR Parkway"
                  value={buildingDetailsOf(form)}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, floor: e.target.value, tower: '', building: '' }));
                  }}
                />
              </label>

              <label className="address-book__field">
                <span>Street area / Locality</span>
                <input
                  className="address-book__input"
                  placeholder="Street, area or locality"
                  value={streetLocalityOf(form)}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, area: e.target.value, locality: '' }));
                  }}
                />
              </label>

              <label className="address-book__field">
                <span>Landmark (optional)</span>
                <input
                  className="address-book__input"
                  placeholder="Nearby landmark"
                  value={form.landmark || ''}
                  onChange={(e) => updateField('landmark', e.target.value)}
                />
              </label>

              <label className="address-book__field">
                <span>Delivery instructions (optional)</span>
                <input
                  className="address-book__input"
                  placeholder="Gate code, directions, etc."
                  value={form.instructions || ''}
                  onChange={(e) => updateField('instructions', e.target.value)}
                />
              </label>

              <p className="address-book__section-label">Receiver details</p>
              <input
                className="address-book__input"
                placeholder="Receiver's name"
                value={form.fullName || ''}
                onChange={(e) => updateField('fullName', e.target.value)}
                autoComplete="name"
              />
              <div className="address-book__phone-row">
                <label className="address-book__field address-book__field--code">
                  <span>Code</span>
                  <select
                    className="address-book__input"
                    value={form.phoneCountryCode || '+91'}
                    onChange={(e) => updateField('phoneCountryCode', e.target.value)}
                    aria-label="Country code"
                  >
                    {PHONE_COUNTRY_CODES.map((row) => (
                      <option key={row.code} value={row.code}>
                        {row.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="address-book__field address-book__field--phone">
                  <span>Phone</span>
                  <input
                    className="address-book__input"
                    placeholder="Receiver's phone"
                    value={form.phone || ''}
                    onChange={(e) => updateField('phone', e.target.value)}
                    autoComplete="tel"
                    inputMode="tel"
                  />
                </label>
              </div>

              <p className="address-book__section-label">Save address as</p>
              <div className="address-book__types" role="group" aria-label="Address type">
                {ADDRESS_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`address-book__type-btn${form.addressType === type ? ' is-active' : ''}`}
                    onClick={() => updateField('addressType', type)}
                  >
                    {type}
                  </button>
                ))}
              </div>

              <label className="address-book__default-check">
                <input
                  type="checkbox"
                  checked={Boolean(form.isDefault) || addresses.length === 0}
                  disabled={addresses.length === 0}
                  onChange={(e) => updateField('isDefault', e.target.checked)}
                />
                <span>Set as default address</span>
              </label>

              <button type="button" className="address-book__save" onClick={handleSave} disabled={saving || !canSave}>
                {saving ? 'Saving…' : 'Save address'}
              </button>
            </div>
          )}

          {error && <p className="address-book__error address-book__error--flow">{error}</p>}
        </div>
        </div>
      )}

      {step === 'list' && error && <p className="address-book__error">{error}</p>}
    </div>
  );
};

export default DeliveryAddressBook;
